import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { documentKeys, documentListQuery } from "@/features/documents/api";
import { DocumentsPage } from "@/features/documents/DocumentsPage";
import { documentListColumns, documentsSearchValidator, type DocumentsSearch } from "@/features/documents/search";
import { TransferPanel } from "@/features/documents/TransferPanel";
import { ImportPanel } from "@/features/import/ImportPanel";
import { warehouseListQuery } from "@/features/warehousing/api";
import { useActiveCompany } from "@/lib/activeCompany";
import { useCan } from "@/lib/session";
import { viewQuery } from "@/lib/listView";

// Stock transfers: list → document panel; "Quick transfer" creates and posts
// in one RPC (post_quick_transfer). Import: a file becomes one draft transfer
// in the active company, posted from its panel (ADR-034).

export const Route = createFileRoute("/_app/warehousing/transfers")({
  validateSearch: documentsSearchValidator(documentListColumns.stock_transfer),
  loaderDeps: ({ search }) => ({ status: search.status, f: search.f, s: search.s }),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchInfiniteQuery(warehouseListQuery(false));
    return context.queryClient.ensureInfiniteQueryData(documentListQuery("stock_transfer", deps.status, viewQuery(documentListColumns.stock_transfer, deps)));
  },
  component: TransfersPage,
});

function TransfersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = useCallback(
    (patch: Partial<DocumentsSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const canImport = useCan("core.data.import");
  const canPost = useCan("inventory.transfer.post");
  const { company } = useActiveCompany();
  return (
    <DocumentsPage
      type="stock_transfer"
      search={search}
      setSearch={setSearch}
      permission="inventory.transfer.post"
      title={t("documents.transfersTitle")}
      breadcrumb={t("nav.warehousing.transfers")}
      newLabel={t("documents.quickTransfer")}
      onImport={canImport && canPost ? () => setSearch({ import: "stock_transfers", doc: undefined }) : undefined}
      aside={
        search.import === "stock_transfers"
          ? {
              label: t("import.title"),
              node: (
                <ImportPanel
                  entities={["stock_transfers"]}
                  initial="stock_transfers"
                  companyId={company?.id}
                  onClose={() => setSearch({ import: undefined })}
                  invalidate={[documentKeys.all]}
                />
              ),
            }
          : undefined
      }
      renderPanel={(docId) => (
        <TransferPanel key={docId} docId={docId} onClose={() => setSearch({ doc: undefined })} onOpen={(id) => setSearch({ doc: id })} />
      )}
    />
  );
}
